import React from "react";
import { render } from "react-dom";

import { ControlComponentProps, ControlValue } from "@multiweb/reactComponents";
import { TypedObject } from "@multiweb/types/react";
import { Event } from "Global/Base/Events";
import { ILanguageChangedEventData, Localization } from "OW.Remoting/Remoting/Localization";
import { GenericControl } from "UI/UI.BaseControls/GenericControl";
import { IControl } from "UI/UI/UI/Controls/ControlInterfaces";
import { ControlUtils } from "UI/UI/UI/Controls/ControlUtils";
import { GenericControlConnector, GenericControlConnectorAttrs } from "../GenericControlConnector";
import { inputIdFromId } from "../utils";

/**
 * V Xgen je nedefinovaný lokalizační klíč reprezentován jako `XG-666`, případně `-666`.
 * Tato konstanta slouží k ověření, zda se jedná o tento klíč.
 * */
const EMPTY_XGEN_LOC_KEY_SUFFIX = "-666";

/**
 * Umožňuje napojení react komponent do původní multiweb aplikace.
 * Do konektoru je skrze konstruktor vložena react komponenta, které jsou následně předány props důležité pro její vykreslování.
 * Konektor implementuje stejná rozhraní jako needitovatelný control {@link Control} a zastupuje jeho funkci.
 */
export class ControlConnector extends GenericControlConnector implements IControl {
    /** Unikátní identifikátor komponenty. Id je určeno primárně pro input element v komponentě. */
    private readonly __componentId: string | undefined;
    /** Událost pro zasílání změn příznáku načítání a zobrazování aplikačních dat */
    protected _canValueLoadChanged: Event<IControl, boolean> = new Event<IControl, boolean>();
    /** Událost pro zasílání změn v hodnotě lokalizační kultůry */
    protected _cultureChanged: Event<IControl, string> = new Event<IControl, string>();
    /** Událost pro zasílání změn příznaku viditelnosti */
    protected _visibilityChanged: Event<IControl, boolean> = new Event<IControl, boolean>();
    /** Jméno komponenty */
    protected _componentName: string = "";
    /** Hodnota lokalizační kultůry pro nastavení formátování */
    protected _culture: string;
    /** Příznak, zda je komponenta povolená */
    protected _enabled: boolean = true;
    /** Přiznak, zda je komponenta nastavena pro čtení */
    protected _enabledVisual = true;
    /** Příznak, zda má komponenta vlastnost být interaktivní */
    protected _interactive = true;
    /** Příznak, zda je vizuální stav komponenty zašifrovaný */
    protected _isVisuallyEncrypted: boolean = false;
    /** Lokalizační klíč názvu komponenty a nápovědy */
    protected _locKey: string;
    /** Příznak, zda je možné komponenta viditelná.*/
    protected _visible: boolean = true;
    /**
     * Příznak, zda má komponenta obalovací container.
     * Používá se k nastavení viditelnosti v metodě {@link _visibilityChangedListener}.
     */
    protected readonly _hasContainerParent: boolean = false;
    /** Poslední hodnota příznaku `canValueLoad` */
    private _lastCanValueLoad = false;
    /** Popisek ke controlu */
    protected _captionLabel: HTMLLabelElement | undefined;
    /** Lokalizační klíč pro placeholder */
    public readonly placeholderLocKey: string;
    private _controlValue: ControlValue;

    /**
     * @param attrs Hlavní atributy konektoru
     * @param canRender Příznak, zda se může komponenta vykreslit hned po inicializaci konektoru.
     * Ve výchozím nastavení (`true`) je komponenta vykreslena po volání {@link _renderIfTrue} v konstruktoru
     */
    constructor(attrs: GenericControlConnectorAttrs, canRender: boolean = true) {
        super(attrs, false);
        this._locKey = ControlUtils.getDataAttr("captionlockey", this.element);
        this.placeholderLocKey = ControlUtils.getDataAttr("placeholderlc", this.element) || "";
        this.__componentId = attrs.componentId;

        if (this.locKey?.endsWith(EMPTY_XGEN_LOC_KEY_SUFFIX)) {
            this._locKey = undefined;
        }
        this._loadComponentName();

        if (this.element.parentElement?.classList.contains("ControlContainer")) {
            this._hasContainerParent = true;
        }

        this.visibilityChanged.add(this._visibilityChangedListener);
        this._eventsController.add(Localization.languageChanged, this._languageChangedListener);

        this._setDefaultTooltip();

        this._setCaptionLabel();

        this.element.setAttribute("data-react-component", "connector");

        this._renderIfTrue(canRender);
    }

    /**
     * Načte jméno komponenty.
     * @private
     */
    private _loadComponentName(): void {
        if (this.locKey) {
            Localization.getTextPromise(this.locKey).then((primaryText) => {
                this._componentName = primaryText;
            });

            this._eventsController.add(Localization.languageChanged, async () => {
                this._componentName = await Localization.getTextPromise(this.locKey);
            });
        } else {
            this._componentName = ControlUtils.getDataAttr("attrname", this.element);
        }
    }

    /**
     * Nastaví nápovědu `title` na {@link GenericControlConnector._rootElement}.
     * @protected
     */
    protected _setDefaultTooltip(): void {
        if (this.locKey) {
            Localization.addLocDescData(this.element, this.locKey, "title");
        }
    }

    /**
     * Nastaví referenci na popisek ke controlu.
     * V současnosti nemá konektor žádnou informaci o popisku ke controlu, proto si na něj musí referenci vytáhnout z DOMu.
     *
     * @private
     */
    protected _setCaptionLabel(): void {
        if (this.element.previousElementSibling
            && this.element.previousElementSibling.classList.contains("CaptionContainer")
            && this.element.previousElementSibling.childElementCount > 0) {
            this._captionLabel = this.element.previousElementSibling.firstElementChild as HTMLLabelElement;
        } else if (this.element.parentElement?.previousElementSibling !== null
            && this.element.parentElement?.previousElementSibling?.classList.contains("caption-component")) {
            this._captionLabel = this.element.parentElement.previousElementSibling as HTMLLabelElement;
        }
    }

    /**
     * Listener pro změnu jazyka.
     * @param _sender
     * @param data
     * @private
     */
    private _languageChangedListener = (_sender: typeof Localization, data: ILanguageChangedEventData) => {
        this.cultureChanged.trigger(this, data.culture);
    };

    /**
     * Listener pro změnu viditelnosti.
     * @param _sender
     * @param isVisible Stav viditelnosti.
     * @private
     */
    private _visibilityChangedListener = (_sender: IControl, isVisible: boolean) => {
        if (!this.tryTriggerCanValueLoadChange(this, this.canValueLoad)) {
            return;
        }

        // Je potřeba zneviditelnit i container v případě kdy jsou přes sebe umístěny komponenty a přepíná se jejich viditelnost.
        if (this._hasContainerParent && this.element.parentElement) {
            this.element.parentElement.style.display = isVisible ? "" : "none";
        }
    };

    private get _componentId(): string | undefined {
        return this.__componentId;
    }

    public get canValueLoadChanged(): Event<IControl, boolean> {
        return this._canValueLoadChanged;
    }

    public set canValueLoadChanged(value: Event<IControl, boolean>) {
        this._canValueLoadChanged = value;
    }

    public get cultureChanged(): Event<IControl, string> {
        return this._cultureChanged;
    }

    public set cultureChanged(value: Event<IControl, string>) {
        this._cultureChanged = value;
    }

    public get visibilityChanged(): Event<IControl, boolean> {
        return this._visibilityChanged;
    }

    public set visibilityChanged(value: Event<IControl, boolean>) {
        this._visibilityChanged = value;
    }

    /** Příznak, zda komponenta může načíst aplikační data a zobrazit je. */
    public get canValueLoad(): boolean {
        return this.visible && GenericControl.allParents(
            this.element,
            (el: HTMLElement) => el.style.display !== "none" || el.classList.contains("AlignPanelSubcomponent"), // obalovaci elementy se preskoci
        );
    }

    public get componentName(): string {
        return this._componentName;
    }

    public set componentName(value: string) {
        this._componentName = value;
    }

    public get culture(): string {
        return this._culture;
    }

    public set culture(value: string) {
        if (this._culture !== value) {
            this._culture = value;
            this.cultureChanged.trigger(this, value);
        }
    }

    public get enabled(): boolean {
        return this._enabled;
    }

    /** Nevolá render, protože při setu enabled se zároveň setuje enabledVisual, který render vyvolá */
    public set enabled(value: boolean) {
        this._enabled = value;
    }

    public get enabledVisual(): boolean {
        return this._enabledVisual;
    }

    public set enabledVisual(value: boolean) {
        if (this._enabledVisual !== value) {
            this._enabledVisual = value;
            this._render();
        }
    }

    public get interactive(): boolean {
        return this._interactive;
    }

    public set interactive(value: boolean) {
        if (this._interactive !== value) {
            this._interactive = value;
            this._render();
        }
    }

    public get isVisuallyEncrypted(): boolean {
        return this._isVisuallyEncrypted;
    }

    public set isVisuallyEncrypted(value: boolean) {
        if (this._isVisuallyEncrypted !== value) {
            this._isVisuallyEncrypted = value;
            this._render();
        }
    }

    public get locKey(): string {
        return this._locKey;
    }

    public set locKey(value: string) {
        if (this._locKey !== value) {
            this._locKey = value;
            this._render();
        }
    }

    public get controlValue(): ControlValue {
        return this._controlValue;
    }
    public set controlValue(value: ControlValue) {
        if (this._controlValue !== value) {
            this._controlValue = value;
            this._render();
        }
    }

    public get visible(): boolean {
        return this._visible;
    }

    /**
     * Nastaví viditelnost controlu.
     * Funkcionalita převzata z {@link Control.visible}.
     */
    public set visible(value: boolean) {
        if (this._visible !== value) {
            this._visible = value;
            const display = value ? "" : "none";
            if (this._hasContainerParent && this.element.parentElement) // parenta chceme zobrazit/schovat pouze pokud je to container
                this.element.parentElement.style.display = display;
            this.element.style.display = display;
            this.visibilityChanged.trigger(this, value);
            this._render();
        }
    }

    /**
     * @inheritDoc
     */
    public tryTriggerCanValueLoadChange(_sender: IControl, value: boolean): boolean {
        if (value === this._lastCanValueLoad) {
            return false;
        }
        this._lastCanValueLoad = value;
        this.canValueLoadChanged.trigger(this, value);
        return true;
    }

    /**
     * Zkonvertuje atributy ovlivňující podobu základní needitovatelné vykreslované react komponenty do props.
     * @protected
     */
    protected override _getRenderProps(): ControlComponentProps {
        return {
            ...super._getRenderProps(),
            locKey: this.locKey,
            placeholderLocKey: this.placeholderLocKey,
            visible: this.visible,
            encrypted: this.isVisuallyEncrypted,
            enabled: this.enabledVisual,
            id: this._componentId ?? inputIdFromId(this.element.id),
            interactive: this.interactive,
            value: this._controlValue,
        };
    }

    /**
     * Vrací objekt, jehož properties jsou všechny data-attributy elementu {@link GenericControlConnector._rootElement}
     * @protected
     */
    protected _getDataAttributes(): TypedObject<string | undefined> {
        return { ...this.element.dataset };
    }

    /**
     * Vyrenderuje komponentu v případě že je viditelná.
     * Měla by se používat s rozumem a volat jen, pokud se opravdu něco změnilo.
     * @protected
     */
    protected override _render(): void {
        render(
            this.visible && (
                <this._rootComponent>
                    <this._component {...this._getRenderProps()} />
                </this._rootComponent>
            ),
            this._rootElement,
        );
    }

    public override dispose(): void {
        this._canValueLoadChanged.removeAll();
        this._cultureChanged.removeAll();
        this._visibilityChanged.removeAll();

        super.dispose();
    }
}
